import type { Page } from '@viva/core';

export type PageFetcher<T> = (offset: number, limit: number) => Promise<Page<T>>;

/**
 * Sparse, page-cached view over a large server-side list. Rows are fetched
 * in pages as a virtualized list asks for them; at most `maxPages` pages are
 * kept, least recently used evicted first.
 */
export class PagedItems<T> {
  total: number | null = null;
  error: unknown = null;
  private readonly pages = new Map<number, T[]>();
  private readonly inFlight = new Set<number>();
  /** False while no component is attached; results are kept but not announced. */
  private active = true;

  constructor(
    private readonly fetchPage: PageFetcher<T>,
    private readonly onChange: () => void,
    readonly pageSize = 100,
    private readonly maxPages = 30,
  ) {}

  get(index: number): T | undefined {
    const page = this.pages.get(Math.floor(index / this.pageSize));
    return page?.[index % this.pageSize];
  }

  /** Requests the pages covering `[start, end]` (inclusive). */
  ensure(start: number, end: number): void {
    if (!this.active) return;
    const first = Math.max(0, Math.floor(start / this.pageSize));
    const last = Math.max(first, Math.floor(end / this.pageSize));
    for (let page = first; page <= last; page++) {
      if (this.total !== null && page * this.pageSize >= this.total) break;
      const cached = this.pages.get(page);
      if (cached) {
        this.pages.delete(page);
        this.pages.set(page, cached);
      } else if (!this.inFlight.has(page)) {
        this.load(page);
      }
    }
  }

  /** Attach/detach; React may detach and re-attach the same instance (StrictMode). */
  setActive(active: boolean): void {
    this.active = active;
  }

  private load(page: number): void {
    this.inFlight.add(page);
    this.fetchPage(page * this.pageSize, this.pageSize).then(
      (result) => {
        this.inFlight.delete(page);
        this.total = result.total;
        this.error = null;
        this.pages.set(page, result.items);
        while (this.pages.size > this.maxPages) {
          const oldest = this.pages.keys().next().value;
          if (oldest === undefined) break;
          this.pages.delete(oldest);
        }
        if (this.active) this.onChange();
      },
      (error: unknown) => {
        this.inFlight.delete(page);
        this.error = error;
        if (this.active) this.onChange();
      },
    );
  }
}
